"use client";

import { useEffect, useState } from "react";
import { computePhysicalSize, type PhysicalSize } from "@/lib/text-metrics";
import { useFontReady } from "@/hooks/use-font-ready";

/**
 * Live, real-world width/height (cm) of `text` set in `fontFamily` at the
 * given physical `heightCm`.
 *
 * The actual measurement needs the Canvas API, which only exists in the
 * browser. To avoid an SSR/client hydration mismatch, the very first
 * render (server *and* first client paint) uses a deterministic
 * placeholder; the real, canvas-measured value is then computed in an
 * effect (client-only, after hydration) and applied via setState - and
 * recomputed again the instant the webfont finishes loading.
 */
export function usePhysicalSize(
  text: string,
  fontFamily: string,
  weight: number,
  heightCm: number
): PhysicalSize & { fontsReady: boolean } {
  const fontsReady = useFontReady(fontFamily, weight);
  const [size, setSize] = useState<PhysicalSize>({
    widthCm: heightCm,
    heightCm,
  });

  useEffect(() => {
    let cancelled = false;
    // Deferred to a microtask (rather than calling setState synchronously
    // in the effect body) so this never triggers cascading render warnings -
    // same pattern as useFontReady.
    Promise.resolve().then(() => {
      if (!cancelled) {
        setSize(computePhysicalSize(text, fontFamily, weight, heightCm));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [text, fontFamily, weight, heightCm, fontsReady]);

  return { ...size, fontsReady };
}
